import React, { useState, useEffect } from 'react';
import './App.css';
import AdminDashboard from './AdminDashboard';

// ============================================
// DATA: Questions and Content
// ============================================

export const part1Questions = [
  {
    id: 'transition_type',
    title: 'Type of move',
    prompt: 'What best describes this transition?',
    options: [
      { value: 1, text: 'Internal promotion within my current area' },
      { value: 2, text: 'Internal move to a different area (lateral)' },
      { value: 3, text: 'Internal promotion into a different area' },
      { value: 3, text: 'External hire at a similar level to my previous role' },
      { value: 4, text: 'External hire with a step up in seniority' },
      { value: 5, text: 'Brought in to turn around or fix a specific situation' }
    ]
  },
  {
    id: 'mandate_clarity',
    title: 'Mandate clarity',
    prompt: 'How clear is your mandate?',
    options: [
      { value: 1, text: 'I have a clear, specific mandate with defined expectations' },
      { value: 2, text: 'I have a general sense of what\'s expected, but little detail' },
      { value: 3, text: 'Expectations seem vague or I\'m hearing different things from different people' },
      { value: 4, text: 'I\'ve been told to figure out what\'s needed' }
    ]
  },
  {
    id: 'time_pressure',
    title: 'Time pressure',
    prompt: 'How much pressure is there to deliver early?',
    options: [
      { value: 1, text: 'I have time to learn before I\'m expected to deliver' },
      { value: 2, text: 'There\'s some expectation of early progress, but it\'s reasonable' },
      { value: 3, text: 'There\'s significant pressure to show results quickly' },
      { value: 4, text: 'I\'ve been brought in to deliver something urgently' }
    ]
  },
  {
    id: 'predecessor',
    title: 'Predecessor situation',
    prompt: 'What\'s the story with your predecessor?',
    options: [
      { value: 2, text: 'This is a new role (there\'s no predecessor)' },
      { value: 1, text: 'My predecessor left on good terms and was seen as successful' },
      { value: 2, text: 'My predecessor left on neutral terms' },
      { value: 3, text: 'My predecessor was pushed out or seen as unsuccessful' },
      { value: 3, text: 'I don\'t know the full story' }
    ]
  },
  {
    id: 'team_stability',
    title: 'Team stability',
    prompt: 'What\'s the state of the team you\'re inheriting?',
    options: [
      { value: 1, text: 'Stable and functioning well' },
      { value: 2, text: 'Stable but underperforming' },
      { value: 3, text: 'In flux: recent changes, departures, or uncertainty' },
      { value: 4, text: 'I need to build or significantly reshape the team' }
    ]
  },
  {
    id: 'external_environment',
    title: 'External environment',
    prompt: 'How would you describe the external environment this organisation is facing? Consider AI adoption, regulatory change, market shifts or demographic pressures.',
    options: [
      { value: 1, text: 'Broadly stable: the main challenges are internal' },
      { value: 2, text: 'Shifting: external changes are underway but direction is clear' },
      { value: 3, text: 'Disrupting: significant external forces are actively changing how the sector operates' },
      { value: 2, text: 'I don\'t have enough visibility yet to answer confidently', isUncertain: true }
    ]
  },
  {
    id: 'timeline',
    title: 'Timeline',
    prompt: 'Where are you in this transition?',
    options: [
      { value: 'within_4_weeks', text: 'About to start: within the next four weeks' },
      { value: 'already_started', text: 'Already in the role: within the first 90 days' },
      { value: 'mid_transition', text: 'Mid-transition and losing momentum' },
      { value: 'preparing', text: 'Preparing for a move I can see coming' }
    ]
  }
];

export const part2Questions = [
  // Foundational Knowledge
  { id: 'f2', dimension: 'foundational', text: 'I know how decisions really get made here, not just the formal process' },
  { id: 'f3', dimension: 'foundational', text: 'I understand the core business model and how value is created' },
  { id: 'f4', dimension: 'foundational', text: 'I\'m familiar with the key systems, processes, and ways of working' },
  // Socio-Political Awareness
  { id: 's1', dimension: 'sociopolitical', text: 'I have a network of relationships I can draw on in this organisation' },
  { id: 's2', dimension: 'sociopolitical', text: 'I understand the political dynamics: who has real influence, who\'s aligned with whom' },
  { id: 's4', dimension: 'sociopolitical', text: 'I know the history: what\'s been tried before, what\'s sensitive, where the old wounds are' },
  // Strategic Clarity
  { id: 'st1', dimension: 'strategic', text: 'I\'m clear on the strategic priorities for my area and how they connect to the wider organisation' },
  { id: 'st2', dimension: 'strategic', text: 'I understand the key resources I have to work with: budget, people, capabilities' },
  { id: 'st4', dimension: 'strategic', text: 'I understand who the critical stakeholders are and what they need from me' }
];

export const part3Questions = [
  {
    id: 1,
    title: 'First week priorities',
    prompt: 'When you start a new leadership role, what do you find yourself drawn to first?',
    options: [
      { stage: 'explore', text: 'Having conversations to get a feel for the people and culture' },
      { stage: 'analyse', text: 'Getting hold of strategy documents, org charts, and data to understand the logic' },
      { stage: 'decide', text: 'Identifying the key decisions that need to be made and who needs to make them' },
      { stage: 'act', text: 'Finding a problem I can get stuck into and make progress on' }
    ]
  },
  {
    id: 4,
    title: 'Forming your initial view',
    prompt: 'What most influences your early impressions of an organisation?',
    options: [
      { stage: 'explore', text: 'The quality of relationships and how people treat each other' },
      { stage: 'analyse', text: 'Whether the strategy and structure make logical sense' },
      { stage: 'decide', text: 'How quickly I can see what needs to be prioritised' },
      { stage: 'act', text: 'How things respond when I start to act' }
    ]
  },
  {
    id: 5,
    title: 'What frustrates you',
    prompt: 'In the first few months of a new role, what frustrates you most?',
    options: [
      { stage: 'explore', text: 'Not having enough access to people or honest conversations' },
      { stage: 'analyse', text: 'Lack of clarity about strategy, metrics, or how success is defined' },
      { stage: 'decide', text: 'Endless discussion without decisions being made' },
      { stage: 'act', text: 'Too much talking and not enough doing' }
    ]
  },
  {
    id: 6,
    title: 'How you test your assumptions',
    prompt: 'When you have a hunch about what\'s going on, how do you test it?',
    options: [
      { stage: 'explore', text: 'I sound it out with people I\'m starting to trust' },
      { stage: 'analyse', text: 'I look for data or evidence that confirms or contradicts it' },
      { stage: 'decide', text: 'I commit to a view and see if anyone pushes back' },
      { stage: 'act', text: 'I act on it and see what the response tells me' }
    ]
  },
  {
    id: 7,
    title: 'Building credibility',
    prompt: 'How do you tend to build credibility in a new role?',
    options: [
      { stage: 'explore', text: 'By showing genuine interest in people and building relationships' },
      { stage: 'analyse', text: 'By asking sharp questions that show I understand the situation' },
      { stage: 'decide', text: 'By making clear decisions and standing behind them' },
      { stage: 'act', text: 'By delivering something tangible early on' }
    ]
  },
  {
    id: 8,
    title: 'What energises you',
    prompt: 'In the early months, what gives you energy?',
    options: [
      { stage: 'explore', text: 'Rich conversations where people open up about what\'s really happening' },
      { stage: 'analyse', text: 'Moments of clarity when patterns start to make sense' },
      { stage: 'decide', text: 'Making a call and seeing people align behind it' },
      { stage: 'act', text: 'Getting something done that makes a visible difference' }
    ]
  },
  {
    id: 10,
    title: 'Your natural tempo',
    prompt: 'How would you describe your natural tempo when transitioning into a new role?',
    options: [
      { stage: 'explore', text: 'I take time to connect with people before forming strong views' },
      { stage: 'analyse', text: 'I move carefully; I want to understand the logic before I commit' },
      { stage: 'decide', text: 'I want clarity quickly; I don\'t like ambiguity to linger' },
      { stage: 'act', text: 'I move fast; I learn by doing, not by waiting' }
    ]
  },
  {
    id: 12,
    title: 'Advice you\'d give yourself',
    prompt: 'If you were coaching yourself through a role transition, what reminder would you most need?',
    options: [
      { stage: 'explore', text: 'Don\'t get so caught up in relationships that you lose sight of the task' },
      { stage: 'analyse', text: 'Don\'t get stuck in analysis; at some point you have to commit' },
      { stage: 'decide', text: 'Don\'t decide too quickly; make sure you\'ve really understood first' },
      { stage: 'act', text: 'Don\'t move so fast that you break things or miss what matters' }
    ]
  }
];

const stageDescriptions = {
  explore: {
    name: 'Explore',
    short: 'Gathering perspectives, sensing the culture, building relationships',
    dwellText: 'You\'re naturally drawn to understanding the people and the culture. You gather perspectives, listen carefully, and build relationships. This gives you rich intelligence about what\'s really going on.',
    dwellRisk: 'The risk is staying here too long, comfortable in exploring and reluctant to commit to a direction. Under time pressure, others may start to wonder when you\'ll land.',
    skipText: 'You may move quickly past building relationships and sensing the culture. What you miss here tends to show up later, as political missteps, misread cultures or allies you didn\'t build.'
  },
  analyse: {
    name: 'Analyse',
    short: 'Analysing, making sense, building a mental model',
    dwellText: 'You want to understand the logic before you commit. You build mental models, look for patterns, and prefer to have a clear framework before acting. This gives you strategic clarity.',
    dwellRisk: 'The risk is over-analysing, waiting for a certainty that won\'t come. Under high pressure, this can leave you behind.',
    skipText: 'You may go from gathering input straight to action, without pulling together what you\'ve learned. Your moves may rest on impressions rather than understanding, and you may miss patterns that would have changed your approach.'
  },
  decide: {
    name: 'Decide',
    short: 'Committing to a direction, closing down options, prioritising',
    dwellText: 'You move decisively from understanding to commitment. You\'re comfortable making calls, closing down options, and giving direction. This gives your team clarity and momentum.',
    dwellRisk: 'The risk is committing too early, before you\'ve fully understood what\'s going on. Decisions made on incomplete information can be hard to undo, especially where the politics are complex.',
    skipText: 'You may put off decisions, hoping more exploring or analysing will make the right path obvious. It rarely does, and your team and stakeholders may be waiting for direction while you carry on gathering and thinking.'
  },
  act: {
    name: 'Act',
    short: 'Testing in practice, delivering, creating momentum',
    dwellText: 'You learn by doing. You\'d rather get stuck in and adapt than spend time planning. This gives you momentum and energy, and teaches you things you can\'t learn any other way.',
    dwellRisk: 'The risk is that in complex or politically sensitive transitions, moving fast before you understand the terrain can create problems that are hard to undo. Early missteps can shape how you\'re seen.',
    skipText: 'You may over-prepare: thinking, analysing and deciding, without quite doing. At some point, action gives you information that no amount of thinking will.'
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Score a section 2 area on a 24-point scale, whatever the number of statements in it
export function areaScore(part2Answers, dimension) {
  const ids = part2Questions.filter(q => q.dimension === dimension).map(q => q.id);
  const sum = ids.reduce((total, id) => total + (part2Answers[id] || 0), 0);
  return Math.round(sum * 4 / ids.length);
}

// Score section 3. Works for most/least answers and for older full rankings.
export function scoreCycle(part3Answers) {
  const stages = ['explore', 'analyse', 'decide', 'act'];
  const cycleScores = { explore: 0, analyse: 0, decide: 0, act: 0 };
  const most = { explore: 0, analyse: 0, decide: 0, act: 0 };
  const least = { explore: 0, analyse: 0, decide: 0, act: 0 };
  Object.values(part3Answers || {}).forEach(rankings => {
    stages.forEach(stage => {
      const rank = rankings[stage];
      if (rank === 1) { cycleScores[stage] += 3; most[stage] += 1; }
      else if (rank === 4) { cycleScores[stage] += 1; least[stage] += 1; }
      else cycleScores[stage] += 2;
    });
  });
  // Ties: dwell goes to the stage chosen "most" more often; skip to the stage chosen "least" more often
  const dwellsIn = [...stages].sort((a, b) => (cycleScores[b] - cycleScores[a]) || (most[b] - most[a]))[0];
  const skips = [...stages].sort((a, b) => (cycleScores[a] - cycleScores[b]) || (least[b] - least[a])).find(st => st !== dwellsIn);
  return { cycleScores, dwellsIn, skips };
}

function getComplexityLabel(score) {
  if (score <= 10) return { label: 'Low complexity', level: 'low' };
  if (score <= 16) return { label: 'Moderate complexity', level: 'moderate' };
  if (score <= 21) return { label: 'High complexity', level: 'high' };
  return { label: 'Very high complexity', level: 'very_high' };
}

function getReadinessLabel(score) {
  if (score <= 10) return { label: 'Low', level: 'low' };
  if (score <= 17) return { label: 'Moderate', level: 'moderate' };
  return { label: 'High', level: 'high' };
}

function getComplexityHeadline(level) {
  const headlines = {
    low: 'A manageable transition',
    moderate: 'A transition with some stretch',
    high: 'A demanding transition',
    very_high: 'A high-stakes transition'
  };
  return headlines[level];
}

// ============================================
// COMPONENTS
// ============================================

function LandingPage({ onStart }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    onStart({ email, name });
  };

  return (
    <div className="landing-page">
      <div className="landing-hero">
        <img src="/assets/PB_Logo_Reverse.png" alt="Paula Broadbent Consulting" className="hero-logo" />
        <div className="landing-content">
          <div className="landing-header">
            <h2 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '16px', color: 'white' }}>Transition Compass</h2>
            <h1>You landed the job. Now land the role.</h1>
            <p className="subtitle">A 5-minute reflection tool for senior leaders in transition</p>
          </div>
        </div>
      </div>

      <div className="landing-body">
        <div className="landing-content">
          <div className="problem-section">
            <p>
              In a new senior role, there's a lot to read at once: how the place works, the people
              and the politics, where the strategy is heading, and what's expected of you.
            </p>
            <p>
              Under pressure, familiar habits tend to take over, whether or not they suit this role.
            </p>
            <p style={{ fontStyle: 'italic', color: '#3a6079' }}>
              No matter how experienced you are, taking time to read the terrain of each transition
              helps you land well.
            </p>
          </div>

          <div className="value-section">
            <h2>What you'll get</h2>
            <p>This takes about 5 minutes. At the end, you'll have:</p>
            
            <div className="value-item">
              <h3>The terrain</h3>
              <p>This reads the context you're stepping into: the type of move, how clear the mandate is, the pressure to deliver, what you're inheriting, and what's happening outside.</p>
            </div>
            
            <div className="value-item">
              <h3>Your understanding of the organisation</h3>
              <p>This shows what you know so far about the organisation you're entering: how it works, the people and politics, and the strategy.</p>
            </div>
            
            <div className="value-item">
              <h3>Your typical approach</h3>
              <p>This shows how you naturally approach a new role: where you tend to dwell, where you tend to skip, and what that means for this transition.</p>
            </div>
            
            <p className="value-summary">
              Together, they show you, in this transition, and where to focus to land well.
            </p>
          </div>

          <div className="audience-section">
            <h2>Who this is for</h2>
            <p>
              This is designed for senior leaders (executives, directors, general managers) in 
              the middle of, or about to begin, a significant role transition.
            </p>
            <p>
              Whether you're stepping up internally, joining from outside, or taking on a turnaround,
              each transition has its own terrain, which the Compass helps you read.
            </p>
          </div>
        </div>
      </div>

      <div className="landing-form-section">
        <h2>Take the Compass</h2>
        <p>Enter your details below. You'll get your full results immediately on screen, and a 
        copy sent to your inbox.</p>
        
        <form onSubmit={handleSubmit} className="landing-form">
          <div className="form-field">
            <label htmlFor="email">Email address *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          
          <div className="form-field">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="So we can personalise your results"
            />
          </div>
          
          {error && <p className="form-error">{error}</p>}
          
          <button type="submit" className="btn-primary">Begin</button>
          
          <p className="form-note">
            It takes about 5 minutes, and your data stays private.
          </p>
        </form>
      </div>

      <div className="landing-footer">
        <p>
          The Transition Compass was developed by Paula Broadbent, an organisational and leadership consultant who works with senior 
          executives navigating role transitions. It draws on established research in adult 
          development and decision styles, adapted specifically for the demands of leadership 
          transitions.
        </p>
        <p className="copyright">&copy; Paula Broadbent Consulting 2026. All rights reserved.</p>
      </div>
    </div>
  );
}
function ProgressBar({ currentPart, totalParts }) {
  return (
    <div className="progress-container">
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${(currentPart / totalParts) * 100}%` }}
        />
      </div>
      <div className="progress-text">Part {currentPart} of {totalParts}</div>
    </div>
  );
}

function Part1Question({ question, answer, onAnswer }) {
  return (
    <div className="question-card">
      <h3 className="question-title">{question.title}</h3>
      <p className="question-prompt">{question.prompt}</p>
      <div className="options-list">
        {question.options.map((option, index) => (
          <button
            key={index}
            className={`option-button ${answer === index ? 'selected' : ''}`}
            onClick={() => onAnswer(question.id, index, option)}
          >
            {option.text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Part1({ answers, onAnswer, onNext }) {
  const allAnswered = part1Questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="diagnostic-part">
      <ProgressBar currentPart={1} totalParts={3} />
      
      <div className="part-header">
        <h2>Part 1: The terrain</h2>
        <p>First, some context about the transition you're facing. This helps us understand what 
        kind of move this is; not all transitions are equally demanding.</p>
      </div>

      <div className="questions-container">
        {part1Questions.map(question => (
          <Part1Question
            key={question.id}
            question={question}
            answer={answers[question.id]?.index}
            onAnswer={(id, index, option) => onAnswer(id, { index, ...option })}
          />
        ))}
      </div>

      <div className="part-navigation">
        <button 
          className="btn-primary" 
          onClick={onNext}
          disabled={!allAnswered}
        >
          Continue to Part 2
        </button>
      </div>
    </div>
  );
}

function Part2({ answers, onAnswer, onNext, onBack }) {
  const likertLabels = [
    'Strongly disagree',
    'Disagree',
    'Slightly disagree',
    'Slightly agree',
    'Agree',
    'Strongly agree'
  ];

  const allAnswered = part2Questions.every(q => answers[q.id] !== undefined);

  const dimensions = [
    { key: 'foundational', title: 'Foundational Knowledge', subtitle: 'How well do you understand how this place works?' },
    { key: 'sociopolitical', title: 'Socio-Political Awareness', subtitle: 'How well do you understand the people and politics?' },
    { key: 'strategic', title: 'Strategic Clarity', subtitle: 'How clear are you on the strategic landscape?' }
  ];

  return (
    <div className="diagnostic-part">
      <ProgressBar currentPart={2} totalParts={3} />
      
      <div className="part-header">
        <h2>Part 2: Your understanding of the organisation</h2>
        <p>Now, some questions about what you already know (or don't yet know) about the 
        organisation and role you're moving into. Be honest rather than aspirational.</p>
      </div>

      <div className="questions-container">
        {dimensions.map(dimension => (
          <div key={dimension.key} className="dimension-section">
            <h3 className="dimension-title">{dimension.title}</h3>
            <p className="dimension-subtitle">{dimension.subtitle}</p>
            
            {part2Questions
              .filter(q => q.dimension === dimension.key)
              .map(question => (
                <div key={question.id} className="likert-question">
                  <p className="likert-text">{question.text}</p>
                  <div className="likert-scale">
                    {likertLabels.map((label, index) => (
                      <button
                        key={index}
                        className={`likert-option ${answers[question.id] === index + 1 ? 'selected' : ''}`}
                        onClick={() => onAnswer(question.id, index + 1)}
                        title={label}
                      >
                        <span className="likert-number">{index + 1}</span>
                        <span className="likert-label">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

      <div className="part-navigation">
        <button className="btn-secondary" onClick={onBack}>Back</button>
        <button 
          className="btn-primary" 
          onClick={onNext}
          disabled={!allAnswered}
        >
          Continue to Part 3
        </button>
      </div>
    </div>
  );
}

function Part3Question({ question, number, rankings, onRankChange, shuffledOptions }) {
  // Two choices per question: the option most like you (1) and the one least like you (4)
  const getChoice = (stage) => rankings[stage] || null;
  const isComplete = Object.values(rankings).includes(1) && Object.values(rankings).includes(4);

  const handleChoose = (stage, rank) => {
    const newRankings = { ...rankings };
    const holder = Object.entries(newRankings).find(([_, r]) => r === rank)?.[0];
    if (holder) delete newRankings[holder];
    if (holder !== stage) newRankings[stage] = rank;
    onRankChange(question.id, newRankings);
  };

  return (
    <div className={`question-card ranking-question ${isComplete ? 'ranking-complete' : ''}`}>
      <div className="question-number">Question {number} of {part3Questions.length}</div>
      <h3 className="question-title">{question.title}</h3>
      <p className="question-prompt">{question.prompt}</p>

      <div className="ranking-footer ranking-footer-top">
        <p className="ranking-hint">Choose the one most like you, and the one least like you</p>
      </div>

      <div className="ranking-options">
        {shuffledOptions.map((option) => {
          const choice = getChoice(option.stage);
          return (
            <div key={option.stage} className={`ranking-option ${choice ? 'ranked' : ''}`}>
              <div className="rank-buttons">
                {[[1, 'Most'], [4, 'Least']].map(([rank, label]) => (
                  <button
                    key={rank}
                    className={`rank-button choice-button ${choice === rank ? 'selected' : ''}`}
                    onClick={() => handleChoose(option.stage, rank)}
                    title={`${label} like you`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="option-text">{option.text}</p>
            </div>
          );
        })}
      </div>

      {isComplete && (
        <div className="ranking-complete-row">
          <p className="ranking-complete-msg">&#10003; Done</p>
        </div>
      )}
    </div>
  );
}

function Part3({ answers, onAnswer, onSubmit, onBack }) {
  const [shuffledQuestions] = useState(() =>
    part3Questions.map(q => ({
      ...q,
      shuffledOptions: shuffleArray(q.options)
    }))
  );

  const allAnswered = part3Questions.every(q => {
    const qAnswers = answers[q.id] || {};
    return Object.values(qAnswers).includes(1) && Object.values(qAnswers).includes(4);
  });

  return (
    <div className="diagnostic-part">
      <ProgressBar currentPart={3} totalParts={3} />

      <div className="part-header">
        <h2>Part 3: Your typical approach</h2>
        <p>Finally, some questions about how you naturally approach new roles. Think about how
        you've actually behaved in past transitions, or how you'd approach one now.</p>
        <p><strong>For each question, choose the option most like you, and the one least like you.</strong></p>
      </div>

      <div className="questions-container">
        {shuffledQuestions.map((question, i) => (
          <Part3Question
            key={question.id}
            number={i + 1}
            question={question}
            rankings={answers[question.id] || {}}
            onRankChange={onAnswer}
            shuffledOptions={question.shuffledOptions}
          />
        ))}
      </div>

      <div className="part-navigation">
        <button className="btn-secondary" onClick={onBack}>Back</button>
        <button 
          className="btn-primary" 
          onClick={onSubmit}
          disabled={!allAnswered}
        >
          View Your Results
        </button>
      </div>
    </div>
  );
}

function CycleChart({ scores }) {
  const chartMax = 3 * part3Questions.length; // most like you scores 3 per question
  const stages = ['explore', 'analyse', 'decide', 'act'];
  const stageNames = { explore: 'Explore', analyse: 'Analyse', decide: 'Decide', act: 'Act' };
  
  const cx = 150;
  const cy = 150;
  const maxRadius = 120;

  const getPoint = (index, value) => {
    const angle = (Math.PI / 2) - (index * Math.PI / 2);
    const normalised = (value / chartMax) * maxRadius;
    return {
      x: cx + Math.cos(angle) * normalised,
      y: cy - Math.sin(angle) * normalised
    };
  };

  const dataPoints = stages.map((stage, i) => getPoint(i, scores[stage]));
  const pathD = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  const gridLevels = [0.25, 0.5, 0.75, 1].map(f => f * chartMax);

  return (
    <div className="cycle-chart">
      <svg viewBox="-20 -10 370 320">
        {/* Grid */}
        {gridLevels.map(level => {
          const points = stages.map((_, i) => getPoint(i, level));
          const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
          return <path key={level} d={d} fill="none" stroke="#d0d0d0" strokeWidth="1" />;
        })}

        {/* Axis lines */}
        {stages.map((_, i) => {
          const end = getPoint(i, chartMax);
          return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#d0d0d0" strokeWidth="1" />;
        })}

        {/* Data shape */}
        <path d={pathD} fill="rgba(58, 96, 121, 0.3)" stroke="#3a6079" strokeWidth="2.5" />

        {/* Data points */}
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="6" fill="#3a6079" stroke="#f7f7f8" strokeWidth="2" />
        ))}

        {/* Labels */}
        {stages.map((stage, i) => {
          const labelPoint = getPoint(i, chartMax * 1.2);
          return (
            <text
              key={stage}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#49354b"
              fontSize="14"
              fontWeight="600"
            >
              {stageNames[stage]}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

function ReadinessBar({ label, score, maxScore = 24 }) {
  const percentage = (score / maxScore) * 100;
  const readiness = getReadinessLabel(score);

  return (
    <div className="readiness-bar">
      <div className="readiness-header">
        <span className="readiness-label">{label}</span>
        <span className="readiness-level">{readiness.label}</span>
      </div>
      <div className="readiness-track">
        <div className="readiness-fill" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function getDynamicInterpretation(complexity, readiness, dwellsIn, skips) {
  const complexityLevel = complexity.level;
  const overallReadiness = readiness.overall.level;
  const sociopoliticalLevel = readiness.sociopolitical.level;
  const strategicLevel = readiness.strategic.level;
  const foundationalLevel = readiness.foundational.level;

  // Find strongest and weakest awareness domains
  const domains = [
    { key: 'foundational', label: 'foundational knowledge', level: foundationalLevel, score: readiness.foundational.score },
    { key: 'sociopolitical', label: 'socio-political awareness', level: sociopoliticalLevel, score: readiness.sociopolitical.score },
    { key: 'strategic', label: 'strategic clarity', level: strategicLevel, score: readiness.strategic.score }
  ];
  const sorted = [...domains].sort((a, b) => b.score - a.score);
  const strongest = sorted[0];
  const weakest = sorted[2];

  // Build the integrated interpretation
  let opener = '';
  let awarenessStatement = '';
  let integration = '';

  // Complexity opener
  if (complexityLevel === 'low') {
    opener = `This is a relatively forgiving transition. The stakes are manageable, and there's room to learn as you go.`;
  } else if (complexityLevel === 'moderate') {
    opener = `This is a transition with some stretch. There are foundations to build on, and some parts will need careful handling.`;
  } else if (complexityLevel === 'high') {
    opener = `You're facing a demanding transition. Your early moves will be noticed, and missteps will be harder to recover from.`;
  } else {
    opener = `This is a high-stakes transition. There's less margin for error than usual, and how you start will shape what becomes possible.`;
  }

  // Awareness statement
  if (strongest.level === weakest.level) {
    const evenTexts = {
      high: `Your understanding of the organisation is strong in all three areas.`,
      moderate: `Your understanding of the organisation is even across the three areas, with no major gaps and no strong foundations yet.`,
      low: `Your understanding of the organisation is still forming in all three areas.`
    };
    awarenessStatement = evenTexts[strongest.level];
  } else {
    awarenessStatement = `Your understanding is strongest in ${strongest.label}, and weaker in ${weakest.label}.`;
  }

  // Integration logic - the "combined, this means..." statements
  const isHighComplexity = (complexityLevel === 'high' || complexityLevel === 'very_high');
  const isLowComplexity = complexityLevel === 'low';

  // High complexity combinations
  if (isHighComplexity && weakest.key === 'sociopolitical' && dwellsIn === 'act') {
    integration = `Combined with your tendency to dwell in Act, there's a real risk here. Moving fast in unfamiliar political terrain, before you understand who holds influence and where the sensitivities are, can create problems that are hard to undo. What you need to know isn't in documents. It's in conversations you haven't had yet.`;
  }
  else if (isHighComplexity && weakest.key === 'sociopolitical' && dwellsIn === 'explore') {
    integration = `Your instinct to spend time with people will serve you well here. It's what builds the political awareness you're missing. The risk isn't moving too fast. It's gathering impressions without pulling them into a clear picture, or putting off decisions while you keep exploring.`;
  }
  else if (isHighComplexity && weakest.key === 'strategic' && dwellsIn === 'act') {
    integration = `Combined with your tendency to move to action quickly, there's a risk of putting energy into the wrong things: quick wins that don't connect to what matters here. Before you act, get clearer on what success looks like.`;
  }
  else if (isHighComplexity && weakest.key === 'strategic' && dwellsIn === 'analyse') {
    integration = `You want to understand the logic before you move, and the strategic picture is unclear. You may find yourself analysing without the right inputs. Look for the people who can give you strategic context before you build your mental model.`;
  }
  else if (isHighComplexity && weakest.key === 'foundational' && dwellsIn === 'act') {
    integration = `Combined with your tendency to learn by doing, there's a risk here. You don't yet know how things work: the systems, the processes, how to get things done. Acting before you understand the machinery can lead to friction, or to workarounds that cost you credibility.`;
  }
  else if (isHighComplexity && overallReadiness === 'low') {
    integration = `With real gaps in your understanding of the organisation, this transition rewards patience over speed. It's worth resisting the pressure to move before you're ready, and spending time exploring and analysing before you act.`;
  }
  else if (isHighComplexity && overallReadiness === 'moderate') {
    integration = `Your understanding is mixed: some foundations, and gaps that could catch you out. Given the stakes, it pays to be deliberate, and to understand what you're working with before you move.`;
  }
  // Low complexity combinations
  else if (isLowComplexity && overallReadiness === 'high' && (dwellsIn === 'analyse' || dwellsIn === 'explore')) {
    integration = `Combined with your tendency to dwell in ${stageDescriptions[dwellsIn].name}, the risk here isn't moving too fast. It's waiting too long. Your team and stakeholders may be waiting for a signal.`;
  }
  else if (isLowComplexity && overallReadiness === 'low' && dwellsIn === 'analyse') {
    integration = `Combined with your tendency to analyse, there's a risk of building your mental model on incomplete foundations. Before you analyse further, explore. The model needs richer material.`;
  }
  else if (isLowComplexity && overallReadiness === 'low' && dwellsIn === 'act') {
    integration = `Your instinct to move fast is less risky here, given the lower stakes, and you're still working with limited knowledge. Make sure you're learning from your actions as well as delivering them, and notice what each move teaches you.`;
  }
  // Moderate complexity combinations
  else if (complexityLevel === 'moderate' && weakest.key === 'strategic' && dwellsIn === 'explore' && skips === 'analyse') {
    integration = `Combined with your tendency to explore without pulling things together, there's a risk of collecting impressions without a clear picture. Before you decide anything, it helps to take what you've gathered and ask what it adds up to, and what patterns you're seeing.`;
  }
  else if (complexityLevel === 'moderate' && dwellsIn === 'decide' && skips === 'explore') {
    integration = `Combined with your tendency to move quickly to commitment, there's a risk of locking in a direction before you've fully understood the ground. Make sure you're deciding with enough input, especially from people who see things differently.`;
  }
  else if (complexityLevel === 'moderate' && weakest.key === 'sociopolitical' && skips === 'explore') {
    integration = `People and politics is your weakest area, and you tend to skip the exploring stage. That's a vulnerable combination: you may make moves without understanding the networks and history that decide whether they land.`;
  }
  // Pattern-specific combinations (any complexity)
  else if (strongest.key === 'sociopolitical' && strongest.level === 'high' && dwellsIn === 'act') {
    integration = `You read the politics well. Combined with your bias for action, you can probably move faster here with less risk. Trust your instincts, and stay alert to any gaps in how the place works and in the strategy.`;
  }
  else if (dwellsIn === 'decide' && skips === 'analyse') {
    integration = `Combined with your tendency to commit quickly without fully analysing, there's a risk of locking into a direction on incomplete understanding. Your decisiveness is valuable. Make sure you're not mistaking confidence for clarity.`;
  }
  else if (dwellsIn === 'analyse' && skips === 'act') {
    integration = `Combined with your tendency to analyse thoroughly before moving, the question is whether this transition rewards that patience. Each round of analysis adds less, and at some point your next insight will come from doing, not thinking.`;
  }
  // Default integration
  else {
    integration = `Consider how your natural pattern fits the ground you're stepping onto. Where might dwelling in ${stageDescriptions[dwellsIn].name} serve you well here? Where might skipping ${stageDescriptions[skips].name} create blind spots, given any gaps in your understanding of the organisation?`;
  }

  return `${opener}\n\n${awarenessStatement}\n\n${integration}`;
}

function getSuggestedFocus(complexity, readiness, dwellsIn, skips) {
  // Generate a specific, actionable prompt
  
  if (dwellsIn === 'explore' && (skips === 'decide' || skips === 'act')) {
    if (readiness.overall.level === 'high') {
      return `You have more clarity than you're giving yourself credit for. Pick one decision you've been putting off and make it this week. Your team needs a signal, and you can adjust later.`;
    }
    return `Your instinct to build relationships is an asset, and at some point you'll need to commit. Before your next stakeholder conversation, write down what you think the priorities should be. Use the conversation to test that view, as well as to listen.`;
  }
  
  if (dwellsIn === 'act' && (skips === 'explore' || skips === 'analyse')) {
    if (complexity.level === 'high' || complexity.level === 'very_high') {
      return `You're moving fast in a situation that rewards patience. Before you launch anything else, find one person who's been here a long time and ask what they've seen people in this role get wrong.`;
    }
    return `Your bias for action serves you well. Before your next initiative, it's worth asking who you haven't talked to yet, and what you might be missing.`;
  }
  
  if (dwellsIn === 'analyse' && skips === 'act') {
    if (complexity.level === 'low') {
      return `This situation may not need as much preparation as you're giving it. Pick one thing you've been analysing and act on it this week. You'll learn more from doing than from thinking.`;
    }
    return `Your thoroughness is an asset, and each round of analysis adds less. Pick one decision where you have enough information, and act on it. Your next insight may come from doing, not thinking.`;
  }
  
  if (dwellsIn === 'decide' && skips === 'explore') {
    return `Your decisiveness is valuable. Make sure you're not deciding before you've understood. Before your next big call, seek out two perspectives you haven't heard yet, especially from people who might disagree.`;
  }
  
  // Default
  return `Pay attention to the stage you tend to skip: ${stageDescriptions[skips].name}. This is where your pattern may create blind spots. It's worth thinking about what spending more time there would look like over the next few weeks.`;
 }
function getCoachingParagraph(complexity, readiness, part1Answers) {
  // Check if external hire based on transition type text
  const transitionText = part1Answers.transition_type?.text || '';
  const isExternalHire = transitionText.includes('External hire') || transitionText.includes('Brought in');
  
  // Find lowest readiness domain
  const scores = {
    foundational: readiness.foundational.score,
    sociopolitical: readiness.sociopolitical.score,
    strategic: readiness.strategic.score
  };
  
  const lowestDomain = Object.entries(scores).sort((a, b) => a[1] - b[1])[0][0];
  const lowestLevel = readiness[lowestDomain].level;
  
  // External hire prefix
  const externalPrefix = isExternalHire 
    ? `As an external hire, you're building knowledge of the organisation, relationships and credibility all at once, with none of them in place yet. Research on external hires found they took around two years to perform as well as colleagues promoted from within (Bidwell, 2011). That isn't about capability. Landing from outside means more to navigate, and less margin for early missteps, which is why support in transition pays off.\n\n`
    : '';
  
  // Coaching paragraphs based on lowest domain and level
  const coachingText = {
    foundational: {
      low: `Your biggest gap is in foundational knowledge: how this organisation works, how decisions get made, the business model and the systems. Without it, you learn how things work through friction rather than foresight. Transition coaching would start there, helping you ask the right questions of the right people, so you build that knowledge in weeks rather than months.`,
      moderate: `Foundational knowledge is where focused attention would help most. You understand some of how this organisation works, and the gaps could catch you out at awkward moments. Transition coaching would help you find what you don't yet know about decision-making, the business model and the systems before those gaps show.`,
      high: `All three areas are strong, and foundational knowledge is a little behind the other two. Transition coaching might explore whether your grasp of how things work here is keeping pace with your read of the politics and the strategy, since the processes often decide whether a move lands.`
    },
    sociopolitical: {
      low: `Your biggest gap is in socio-political awareness: the relationships, the politics and the history that shape how things really happen here. Without it, it's hard to turn understanding into influence. Transition coaching would start there, helping you build relationships with intent and read the dynamics quickly, so you're not learning the politics through trial and error.`,
      moderate: `Socio-political awareness is where focused attention would help most. You have some relationships and some political insight, and the gaps could cause friction when you need to influence beyond your own authority. Transition coaching would help you find the blind spots, such as dynamics you're not seeing or relationships you haven't yet prioritised, before they cost you.`,
      high: `All three areas are strong, and socio-political awareness is a little behind the other two. Transition coaching might explore whether your clarity on how things work and where they're going is running ahead of the relational groundwork that decides whether plans gain traction. The clearer we are on what needs doing, the less patience we can have for bringing people with us.`
    },
    strategic: {
      low: `Your biggest gap is in strategic clarity: the priorities, the resources and what stakeholders expect of you. Without it, it's hard to direct your effort towards what matters. Transition coaching would start there, cutting through the ambiguity to what you're really being asked to deliver and what you have to work with.`,
      moderate: `Strategic clarity is where focused attention would help most. You can see some of the priorities, resources and expectations, and the gaps could make your early moves more tentative than they need to be, or out of step with things you haven't yet discovered. Transition coaching would help you turn partial clarity into confident strategic moves.`,
      high: `All three areas are strong, and strategic clarity is a little behind the other two. You understand how things work and can read the politics, and may be less sure where it all needs to go. Transition coaching might focus on sharpening that line of sight, so your effort goes into the priorities that will define success in this role.`
    }
  };
  
  return externalPrefix + coachingText[lowestDomain][lowestLevel];
}

function Results({ userData, part1Answers, part2Answers, part3Answers, onSubmit }) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState('');

  const handleSubmitClick = async () => {
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData,
          part1Answers,
          part2Answers,
          part3Answers,
          timestamp: new Date().toISOString()
        })
      });
      setSubmitMessage('Results submitted successfully! Check your email for your profile.');
    } catch (error) {
      console.error('Failed to submit:', error);
      setSubmitMessage('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Calculate complexity score
  const complexityScore = Object.values(part1Answers).reduce((sum, a) => sum + (typeof a.value === 'number' ? a.value : 0), 0);
  
  // If all complicating factors score below 3 AND transition type is below 3, force to low complexity
  const transitionTypeValue = part1Answers.transition_type?.value || 0;
  const mandateClarityValue = part1Answers.mandate_clarity?.value || 0;
  const timePressureValue = part1Answers.time_pressure?.value || 0;
  const predecessorValue = part1Answers.predecessor?.value || 0;
  const teamStabilityValue = part1Answers.team_stability?.value || 0;
  
  let complexity;
  if (transitionTypeValue < 3 && mandateClarityValue < 3 && timePressureValue < 3 && predecessorValue < 3 && teamStabilityValue < 3) {
    complexity = { label: 'Low complexity', level: 'low' };
  } else {
    complexity = getComplexityLabel(complexityScore);
  }

  // Calculate readiness scores
  const foundationalScore = areaScore(part2Answers, 'foundational');
  const sociopoliticalScore = areaScore(part2Answers, 'sociopolitical');
  const strategicScore = areaScore(part2Answers, 'strategic');
  const overallReadinessScore = Math.round((foundationalScore + sociopoliticalScore + strategicScore) / 3);

  const readiness = {
    foundational: { score: foundationalScore, ...getReadinessLabel(foundationalScore) },
    sociopolitical: { score: sociopoliticalScore, ...getReadinessLabel(sociopoliticalScore) },
    strategic: { score: strategicScore, ...getReadinessLabel(strategicScore) },
    overall: { score: overallReadinessScore, ...getReadinessLabel(overallReadinessScore) }
  };

  // Calculate cycle scores: most like you = 3, not chosen = 2, least like you = 1
  const { cycleScores, dwellsIn, skips } = scoreCycle(part3Answers);

  // Get dynamic content
  const dynamicInterpretation = getDynamicInterpretation(complexity, readiness, dwellsIn, skips);
  const suggestedFocus = getSuggestedFocus(complexity, readiness, dwellsIn, skips);
const coachingParagraph = getCoachingParagraph(complexity, readiness, part1Answers);

  const isExternalHire = (part1Answers.transition_type?.text || '').toLowerCase().includes('external');
  const isAboveThreshold = (
    isExternalHire ||
    complexity.level === 'high' || complexity.level === 'very_high' ||
    part1Answers.external_environment?.value === 3 ||
    part1Answers.timeline?.value === 'within_4_weeks' ||
    part1Answers.timeline?.value === 'already_started'
  );

  // Build complexity context description
  const getComplexityContext = () => {
    const factors = [];
    
    const transitionType = part1Answers.transition_type?.text;
    if (transitionType) factors.push(transitionType.toLowerCase());
    
    const mandateClarity = part1Answers.mandate_clarity?.value;
    if (mandateClarity >= 3) factors.push('unclear mandate');
    
    const timePressure = part1Answers.time_pressure?.value;
    if (timePressure >= 3) factors.push('significant time pressure');
    
    const predecessor = part1Answers.predecessor?.value;
    if (predecessor >= 3) factors.push('a complicated predecessor situation');
    
    const team = part1Answers.team_stability?.value;
    if (team >= 3) factors.push('a team in flux');
    
    const external = part1Answers.external_environment?.value;
    if (external >= 3) factors.push('a disrupted external environment');
    
    if (factors.length === 0) return 'Your transition context is relatively straightforward.';
    
    return `Your transition involves ${factors.slice(0, -1).join(', ')}${factors.length > 1 ? ' and ' : ''}${factors[factors.length - 1]}.`;
  };

  const displayName = userData.name || 'there';

  return (
    <div className="results-page">
      <div className="results-content">
        <div className="results-header">
          <h1>Your Transition Compass</h1>
          <p className="results-intro">
            Hello{userData.name ? ` ${userData.name}` : ''}. Here's what your answers show about the ground you're stepping onto,
            and how you're likely to approach it.
          </p>
        </div>

        {/* Section 4: Dynamic Interpretation */}
<section className="results-section">
  <h2>You, in this transition</h2>
  <div className="dynamic-interpretation">
    {dynamicInterpretation.split('\n\n').map((para, i) => (
      <p key={i}>{para}</p>
    ))}
  </div>
  
  {part1Answers.external_environment?.value === 3 && readiness.sociopolitical.level === 'low' && (
    <div className="combination-trigger" style={{marginTop: '16px'}}>
      <p>External disruption combined with limited socio-political awareness is worth naming directly. In a disrupting environment, the informal dynamics matter more than the formal structure: who has influence, whose agenda is driving decisions, and where the resistance to change sits. It's hard to find your way across shifting ground without understanding the people on it.</p>
    </div>
  )}

  {part1Answers.external_environment?.value === 3 && dwellsIn === 'explore' && (
    <div className="combination-trigger" style={{marginTop: '16px'}}>
      <p>In a disrupting environment, your instinct to gather and listen before committing is both an asset and a risk. What you learn will be valuable. The risk is that the environment moves faster than you're comfortable committing, and that others read extended exploring as a lack of direction, just when they need to see one.</p>
    </div>
  )}

  {part1Answers.external_environment?.value === 3 && (dwellsIn === 'decide' || dwellsIn === 'act') && (
    <div className="combination-trigger" style={{marginTop: '16px'}}>
      <p>In a disrupting environment, your natural pace can work in your favour, or lead to early missteps that are expensive to recover from. The pressure to move quickly will feel familiar and justified. The question worth sitting with is whether the speed serves the situation or your autopilot. Here, moving deliberately tends to serve you better than moving fast.</p>
    </div>
  )}

  {(part1Answers.external_environment?.value === 2 || part1Answers.external_environment?.value === 3) && !part1Answers.external_environment?.isUncertain && readiness.strategic.level === 'low' && (
    <div className="combination-trigger" style={{marginTop: '16px'}}>
      <p>Stepping into an organisation going through external change, without a clear view of the strategy, is the highest-risk starting point the Compass identifies. It's not unusual, since strategic clarity is often one of the last things to become visible from outside. It does mean your early weeks need to go on building that picture, quickly and honestly, before you commit to assumptions that turn out to be wrong.</p>
    </div>
  )}
</section>

        {/* Section 5: Suggested Focus */}
        <section className="results-section focus-section">
          <h2>Your focus for this transition</h2>
          <blockquote className="focus-quote">
            {suggestedFocus}
          </blockquote>
        </section>


        {/* Where coaching could help */}
<section className="results-section">
  <h2>Where coaching could help</h2>
  <div className="coaching-paragraph">
    {coachingParagraph.split('\n\n').map((para, i) => (
      <p key={i}>{para}</p>
    ))}
  </div>
</section>


        {/* Section 6: What Now */}
        <section className="results-section cta-section">
          <h2>What next</h2>
          <p className="cta-headline">How you land in a new senior role shapes everything that follows.</p>
          {isAboveThreshold ? (
            <p>Your answers suggest this transition carries real complexity. Land Well Executive Coaching is built for this.</p>
          ) : (
            <p>If a thinking partner would help with what comes next, Land Well Executive Coaching may be worth a look.</p>
          )}
          <div className="cta-box">
            <button 
              onClick={handleSubmitClick} 
              disabled={isSubmitting}
              className="btn-primary"
              style={{ marginRight: '12px' }}
            >
              {isSubmitting ? 'Emailing your results...' : 'Email yourself a copy of the results'}
            </button>
            <a href="https://www.paulabroadbent.com/landwell" target="_blank" rel="noopener noreferrer" className="btn-primary">
              {isAboveThreshold ? 'See how Land Well works' : 'Find out more about Land Well'}
            </a>
          </div>
          {submitMessage && (
            <p style={{ marginTop: '16px', fontSize: '14px', color: submitMessage.includes('successfully') ? '#2e7d32' : '#c62828' }}>
              {submitMessage}
            </p>
          )}
        </section>

        {/* The detail, area by area */}
        <div className="results-section detail-intro">
          <h2>The detail, area by area</h2>
        </div>

        {/* Section 1: Transition Context */}
        <section className="results-section">
          <h2>The terrain</h2>
          <h3>{getComplexityHeadline(complexity.level)}</h3>
          <p className="complexity-context">{getComplexityContext()}</p>
          <div className="complexity-badge">
            <span className={`badge badge-${complexity.level}`}>{complexity.label}</span>
          </div>
          {part1Answers.external_environment && (
            <div className="external-environment-text" style={{marginTop: '16px'}}>
              {part1Answers.external_environment.value === 1 && (
                <p>The external environment gives you one advantage: it isn't shifting underneath you while you find your footing. The complexity here is mostly internal: organisational, relational and strategic. That's navigable, and your early focus can go on reading the system you're stepping into.</p>
              )}
              {part1Answers.external_environment.value === 2 && !part1Answers.external_environment.isUncertain && (
                <>
                  <p>The direction may be clear for now.</p>
                  <p>External change can unsettle that clarity, often from the inside, as people start to disagree about how fast to move, where to invest and what to protect.</p>
                  <p>Part of reading this organisation is noticing where that's already starting.</p>
                </>
              )}
              {part1Answers.external_environment.value === 3 && (
                <>
                  <p>This makes a demanding transition harder.</p>
                  <p>When outside forces are changing how the sector works, the pressure inside rises. Decisions get more political, priorities move faster, and the strategy you're walking into may look different in six months.</p>
                  <p>If AI is part of that disruption, it's harder still.</p>
                  <p>The organisation may be dealing with skills gaps, resistance, unclear ownership of decisions and uncertainty about where the value lies, all at once. As someone new, you won't yet know it well enough to see what's happening beneath the official story.</p>
                  {(readiness.foundational.level !== 'high' || readiness.sociopolitical.level !== 'high') && (
                    <p>Any gaps in how well you know the organisation and its people matter more here. When everything is moving, misreading it early costs more.</p>
                  )}
                  <p>Pressure also makes your automatic responses more likely to take over. It helps to know what they are before the pressure peaks.</p>
                </>
              )}
              {part1Answers.external_environment.value === 2 && part1Answers.external_environment.isUncertain && (
                <>
                  <p>Not knowing the external context before you enter is itself useful information. It means part of your early work will be building a picture of what the organisation is actually navigating; not just internally but in relation to its market, its sector and the forces acting on both. Leaders who arrive assuming stability and discover disruption mid-transition are the ones who get caught.</p>
                  <p>One question worth asking before you start: is the strategic clarity you have described based on a stable external picture, or has it been stress-tested against the possibility that the environment shifts further?</p>
                </>
              )}
            </div>
          )}
        </section>

        {/* Section 2: Organisational Awareness Profile */}
        <section className="results-section">
          <h2>Your understanding of the organisation</h2>
          <p>This shows how well you know the organisation so far, across three areas: foundational knowledge, socio-political awareness and strategic clarity.</p>
          
          <div className="readiness-bars">
            <ReadinessBar label="Foundational Knowledge" score={foundationalScore} />
            <ReadinessBar label="Socio-Political Awareness" score={sociopoliticalScore} />
            <ReadinessBar label="Strategic Clarity" score={strategicScore} />
          </div>

          <div className="readiness-interpretations">
            <div className="readiness-item">
              <h4>Foundational Knowledge</h4>
              <p>
                {readiness.foundational.level === 'high' && 
                  "You understand how this organisation works: the processes, how things get done."}
                {readiness.foundational.level === 'moderate' && 
                  "You understand some of how this organisation works, and there are still gaps."}
                {readiness.foundational.level === 'low' && 
                  "You're still learning how this place works: the systems, processes, and how decisions get made."}
              </p>
            </div>
            <div className="readiness-item">
              <h4>Socio-Political Awareness</h4>
              <p>
                {readiness.sociopolitical.level === 'high' && 
                  "You understand the politics: who has influence, what's unspoken, where the history is."}
                {readiness.sociopolitical.level === 'moderate' && 
                  "You have some sense of the political landscape, and there's more to learn."}
                {readiness.sociopolitical.level === 'low' && 
                  "The political terrain is still largely unknown to you; this is where surprises tend to come from."}
              </p>
            </div>
            <div className="readiness-item">
              <h4>Strategic Clarity</h4>
              <p>
                {readiness.strategic.level === 'high' && 
                  "You're clear on the strategic priorities, resources, and key stakeholders."}
                {readiness.strategic.level === 'moderate' && 
                  "You have a sense of the strategic landscape but it's not yet fully clear."}
                {readiness.strategic.level === 'low' && 
                  "The strategic picture is still forming; priorities, resources, and stakeholders need more attention."}
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Transition Approach */}
        <section className="results-section">
          <h2>Your typical approach</h2>
          <p>Leaders move through a natural cycle when entering new roles: exploring, analysing, deciding and acting. We all have preferences: stages where we dwell, and stages we move through quickly or skip.</p>
          <p>These patterns aren't good or bad in themselves. What matters is how well your natural approach fits this transition. Here's what your answers suggest.</p>
          <p className="pattern-headline">
            Your pattern: you tend to dwell in <strong>{stageDescriptions[dwellsIn].name}</strong> and 
            skip <strong>{stageDescriptions[skips].name}</strong>
          </p>

          <div className="cycle-container">
            <CycleChart scores={cycleScores} />
            
            <div className="stage-descriptions">
              <div className="stage-item">
                <span className="stage-name">Explore</span>
                <span className="stage-desc">{stageDescriptions.explore.short}</span>
              </div>
              <div className="stage-item">
                <span className="stage-name">Analyse</span>
                <span className="stage-desc">{stageDescriptions.analyse.short}</span>
              </div>
              <div className="stage-item">
                <span className="stage-name">Decide</span>
                <span className="stage-desc">{stageDescriptions.decide.short}</span>
              </div>
              <div className="stage-item">
                <span className="stage-name">Act</span>
                <span className="stage-desc">{stageDescriptions.act.short}</span>
              </div>
            </div>
          </div>

          <div className="pattern-interpretation">
            <p>{stageDescriptions[dwellsIn].dwellText}</p>
            <p>{stageDescriptions[dwellsIn].dwellRisk}</p>
            <p><strong>The stage you're most likely to skip: {stageDescriptions[skips].name}.</strong> {stageDescriptions[skips].skipText}</p>
          </div>
        </section>

      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================

function App() {
  const [currentView, setCurrentView] = useState('landing');
  const [userData, setUserData] = useState({});
  const [part1Answers, setPart1Answers] = useState({});
  const [part2Answers, setPart2Answers] = useState({});
  const [part3Answers, setPart3Answers] = useState({});
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('admin')) {
      window.history.replaceState({}, '', window.location.pathname);
      setShowAdminPrompt(true);
      setAdminPasswordInput('');
      setAdminPasswordError('');
    }
  }, []);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/submissions', {
        headers: { 'X-Admin-Key': adminPasswordInput }
      });
      if (res.status === 401) {
        setAdminPasswordError('Incorrect password.');
        return;
      }
      setAdminKey(adminPasswordInput);
      setShowAdminPrompt(false);
      setAdminPasswordInput('');
      setAdminPasswordError('');
      setCurrentView('admin');
    } catch {
      setAdminPasswordError('Could not connect. Please try again.');
    }
  };

  const handleStart = (data) => {
    setUserData(data);
    setCurrentView('part1');
  };

  const handlePart1Answer = (questionId, answer) => {
    setPart1Answers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handlePart2Answer = (questionId, value) => {
    setPart2Answers(prev => ({ ...prev, [questionId]: value }));
  };

  const handlePart3Answer = (questionId, rankings) => {
    setPart3Answers(prev => ({ ...prev, [questionId]: rankings }));
  };

  const handleSubmit = async () => {
    // In production, this would send data to the backend
    // For now, just show results
    setCurrentView('results');

    // Send data to backend
    try {
      await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData,
          part1Answers,
          part2Answers,
          part3Answers,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to save results:', error);
      // Still show results even if save fails
    }
  };

  return (
    <div className="app">
      {currentView !== 'landing' && currentView !== 'admin' && (
        <header className="app-header-new">
          <img src="/assets/PB_Consulting_Logo_New.png" alt="Paula Broadbent Consulting" className="header-logo-new" />
        </header>
      )}
      {currentView === 'landing' && (
        <LandingPage onStart={handleStart} />
      )}
      
      {currentView === 'part1' && (
        <Part1
          answers={part1Answers}
          onAnswer={handlePart1Answer}
          onNext={() => setCurrentView('part2')}
        />
      )}
      
      {currentView === 'part2' && (
        <Part2
          answers={part2Answers}
          onAnswer={handlePart2Answer}
          onNext={() => setCurrentView('part3')}
          onBack={() => setCurrentView('part1')}
        />
      )}
      
      {currentView === 'part3' && (
        <Part3
          answers={part3Answers}
          onAnswer={handlePart3Answer}
          onSubmit={handleSubmit}
          onBack={() => setCurrentView('part2')}
        />
      )}
      
      {currentView === 'results' && (
        <Results
          userData={userData}
          part1Answers={part1Answers}
          part2Answers={part2Answers}
          part3Answers={part3Answers}
          onSubmit={handleSubmit}
        />
      )}

      {currentView === 'admin' && (
        <AdminDashboard adminKey={adminKey} onBack={() => { setCurrentView('landing'); setAdminKey(''); }} />
      )}

      {showAdminPrompt && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:'8px',padding:'40px',maxWidth:'360px',width:'90%',boxShadow:'0 8px 32px rgba(0,0,0,0.18)'}}>
            <h2 style={{fontFamily:"'Roboto',sans-serif",color:'#49354b',margin:'0 0 8px',fontSize:'20px'}}>Admin access</h2>
            <p style={{margin:'0 0 24px',color:'#666',fontSize:'14px',lineHeight:'1.5'}}>Enter your admin password to continue.</p>
            <form onSubmit={handleAdminLogin}>
              <input
                type="password"
                autoFocus
                value={adminPasswordInput}
                onChange={e => { setAdminPasswordInput(e.target.value); setAdminPasswordError(''); }}
                placeholder="Password"
                style={{width:'100%',padding:'10px 12px',border:'1px solid #cec8ce',borderRadius:'4px',fontSize:'15px',boxSizing:'border-box',marginBottom:'8px',outline:'none'}}
              />
              {adminPasswordError && (
                <p style={{margin:'0 0 12px',color:'#c0392b',fontSize:'13px'}}>{adminPasswordError}</p>
              )}
              <div style={{display:'flex',gap:'10px',marginTop:'16px'}}>
                <button type="submit" style={{flex:1,padding:'10px',background:'#49354b',color:'#fff',border:'none',borderRadius:'4px',fontSize:'14px',fontWeight:600,cursor:'pointer'}}>Enter</button>
                <button type="button" onClick={() => { setShowAdminPrompt(false); setAdminPasswordInput(''); setAdminPasswordError(''); }} style={{flex:1,padding:'10px',background:'#f0f0f0',color:'#333',border:'none',borderRadius:'4px',fontSize:'14px',cursor:'pointer'}}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;